(() => {
  if (window.BsrCollectionScrollRestore) return;

  const STORAGE_KEY = 'bsrCollectionProductReturn:v2';
  const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  const MAX_RESTORE_TIME = 8000;
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';
  const CARD_SELECTOR = 'card-product, .card-product';

  let restoreFrame = 0;
  let restoreTimer = 0;
  let restoreObserver = null;
  let resizeObserver = null;
  let restoreDeadline = 0;
  let activePayload = null;
  let stablePasses = 0;
  let previousScrollRestoration = 'auto';
  let ownsScrollRestoration = false;
  let restoreGeneration = 0;
  let targetCard = null;
  let documentReturnHandled = false;
  let restoreStartedAt = 0;
  let layoutStableAt = 0;
  let cacheRefreshObserved = false;

  const restoreRoot = (payload) => (payload.panelId && document.getElementById(payload.panelId)) || document.getElementById('product-grid') || document.querySelector('main');
  const dynamicLayoutPending = (payload) => {
    if (!payload.panelId) return Boolean(document.getElementById('product-grid')?.querySelector('[data-bsr-cached-card]') && document.querySelector('collection-load-more')?.loading);
    const panel = document.getElementById(payload.panelId);
    if (['restoring', 'cached'].includes(panel?.closest('.bsr-dynamic-menu')?.dataset.bsrReturnCache)) return true;
    const panels = Array.from(panel?.closest('.bsr-dynamic-menu__sections')?.querySelectorAll('[data-bsr-dynamic-menu-panel]') || []);
    const index = panels.indexOf(panel);
    return panels.slice(0, index + 1).some((candidate) => ['queued', 'hydrating', 'filtering'].includes(candidate.dataset.bsrDynamicMenuHydration));
  };
  const visibleImagesPending = (payload) => Array.from(restoreRoot(payload)?.querySelectorAll('img[data-image-primary]') || []).some((image) => {
    const rect = image.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && !image.complete;
  });

  const sourceUrl = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const normalizeProductUrl = (value) => {
    try {
      const url = new URL(value, window.location.origin);
      return `${url.pathname}${url.search}`;
    } catch (error) {
      return value || '';
    }
  };
  const getProductHandle = (value) => {
    const path = normalizeProductUrl(value).split('?')[0];
    return path.includes('/products/') ? path.split('/products/')[1].split('/')[0] : '';
  };
  const readPayload = () => {
    try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'); }
    catch (error) { return null; }
  };
  const writePayload = (payload) => {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); }
    catch (error) { /* Storage can be unavailable in restricted browsing modes. */ }
  };
  const clearPayload = () => {
    try { window.localStorage.removeItem(STORAGE_KEY); }
    catch (error) { /* Storage can be unavailable in restricted browsing modes. */ }
  };
  const findCard = (payload) => Array.from((restoreRoot(payload) || document).querySelectorAll(CARD_SELECTOR)).find((card) => {
    const cardUrl = normalizeProductUrl(card.dataset.productUrl || card.querySelector(PRODUCT_LINK_SELECTOR)?.getAttribute('href'));
    return cardUrl === payload.productUrl || (payload.productHandle && getProductHandle(cardUrl) === payload.productHandle);
  }) || null;

  const stopRestore = ({ consume = false } = {}) => {
    if (restoreFrame) window.cancelAnimationFrame(restoreFrame);
    if (restoreTimer) window.clearTimeout(restoreTimer);
    restoreObserver?.disconnect();
    resizeObserver?.disconnect();
    if (ownsScrollRestoration) window.history.scrollRestoration = previousScrollRestoration;
    ownsScrollRestoration = false;
    restoreGeneration += 1;
    targetCard = null;
    restoreFrame = 0;
    restoreTimer = 0;
    restoreObserver = null;
    resizeObserver = null;
    restoreDeadline = 0;
    activePayload = null;
    stablePasses = 0;
    layoutStableAt = 0;
    cacheRefreshObserved = false;
    restoreStartedAt = 0;
    if (consume) clearPayload();
  };

  const scheduleRestorePass = (delay = 0) => {
    if (!activePayload) return;
    if (restoreTimer) window.clearTimeout(restoreTimer);
    restoreTimer = window.setTimeout(() => {
      restoreTimer = 0;
      restorePass();
    }, delay);
  };

  const restorePass = () => {
    const pending = activePayload && dynamicLayoutPending(activePayload);
    if (pending && !cacheRefreshObserved) {
      cacheRefreshObserved = true;
      restoreDeadline = restoreStartedAt + 30000;
    }
    if (!activePayload || Date.now() > restoreDeadline) {
      stopRestore({ consume: true });
      return;
    }
    const card = targetCard?.isConnected ? targetCard : findCard(activePayload);
    if (!card) {
      scheduleRestorePass(160);
      return;
    }
    targetCard = card;
    const targetTop = Number.isFinite(activePayload.cardTop) ? activePayload.cardTop : Math.min(96, window.innerHeight * 0.2);
    const delta = card.getBoundingClientRect().top - targetTop;
    if (Math.abs(delta) > 1) {
      stablePasses = 0;
      layoutStableAt = 0;
      window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior: 'auto' });
    } else if (!pending && !visibleImagesPending(activePayload)) {
      stablePasses += 1;
      if (!layoutStableAt) layoutStableAt = Date.now();
    } else {
      stablePasses = 0;
      layoutStableAt = 0;
    }
    if (stablePasses >= (activePayload.panelId ? 8 : 2) && (!cacheRefreshObserved || Date.now() - layoutStableAt >= 400)) {
      stopRestore({ consume: true });
      return;
    }
    scheduleRestorePass(40);
  };

  const beginRestore = (payload) => {
    stopRestore();
    activePayload = payload;
    restoreStartedAt = Date.now();
    restoreDeadline = restoreStartedAt + (payload.panelId || restoreRoot(payload)?.querySelector('[data-bsr-cached-card]') ? 30000 : MAX_RESTORE_TIME);
    if ('scrollRestoration' in window.history) {
      previousScrollRestoration = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      ownsScrollRestoration = true;
    }
    if (payload.panelId) window.dispatchEvent(new CustomEvent('bsr:product-return-restore-request', {
      detail: { panelId: payload.panelId || '', productUrl: payload.productUrl, productHandle: payload.productHandle || '' }
    }));
    restoreObserver = new MutationObserver(() => {
      stablePasses = 0;
      layoutStableAt = 0;
      if (restoreFrame) return;
      restoreFrame = window.requestAnimationFrame(() => {
        restoreFrame = 0;
        scheduleRestorePass();
      });
    });
    const panelRoot = restoreRoot(payload);
    const root = (payload.panelId && panelRoot?.closest('.bsr-dynamic-menu__sections')) || panelRoot;
    if (root) restoreObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-bsr-dynamic-menu-hydration'] });
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => {
        stablePasses = 0;
        layoutStableAt = 0;
        scheduleRestorePass();
      });
      if (root) resizeObserver.observe(root);
    }
    restoreFrame = window.requestAnimationFrame(() => {
      restoreFrame = 0;
      window.scrollTo({ top: Math.max(0, payload.scrollY || 0), behavior: 'auto' });
      restorePass();
    });
  };

  const shouldSaveClick = (event, link) => {
    if (event.defaultPrevented || event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (link.target && link.target.toLowerCase() === '_blank') return false;
    return normalizeProductUrl(link.href).includes('/products/');
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest(PRODUCT_LINK_SELECTOR);
    if (!link || !shouldSaveClick(event, link)) return;
    const card = link.closest(CARD_SELECTOR);
    const panel = link.closest('[data-bsr-dynamic-menu-panel]');
    const productUrl = normalizeProductUrl(link.href);
    writePayload({
      sourceUrl: sourceUrl(),
      productUrl,
      productHandle: getProductHandle(productUrl),
      panelId: panel?.id || '',
      cardTop: card?.getBoundingClientRect().top ?? Math.min(96, window.innerHeight * 0.2),
      scrollY: window.scrollY || document.documentElement.scrollTop || 0,
      savedAt: Date.now(),
      pending: true
    });
  });

  const cancelForUserIntent = () => stopRestore({ consume: true });
  window.addEventListener('wheel', cancelForUserIntent, { passive: true });
  window.addEventListener('touchstart', cancelForUserIntent, { passive: true });
  window.addEventListener('pointerdown', cancelForUserIntent, { passive: true });
  window.addEventListener('keydown', cancelForUserIntent);

  window.addEventListener('pagehide', () => stopRestore());
  const handleReturn = (event) => {
    if (!event.persisted && documentReturnHandled) return;
    const payload = readPayload();
    if (!payload || !payload.pending || payload.sourceUrl !== sourceUrl()) return;
    if (!Number.isFinite(payload.savedAt) || Date.now() - payload.savedAt > MAX_AGE) {
      clearPayload();
      return;
    }
    documentReturnHandled = true;
    stopRestore();
    const generation = restoreGeneration;
    window.requestAnimationFrame(async () => {
      if (generation !== restoreGeneration) return;
      if (event.persisted) {
        const card = findCard(payload);
        if (card && Math.abs(card.getBoundingClientRect().top - payload.cardTop) <= 4) {
          clearPayload();
          return;
        }
      }
      const loader = window.BsrCollectionLoadMoreRestore;
      if (!payload.panelId && ['error', 'cancelled'].includes(loader?.status)) return;
      if (!payload.panelId && loader?.pending) {
        const result = await loader.promise;
        if (generation !== restoreGeneration || result.status !== 'ready') return;
      }
      if (generation === restoreGeneration) beginRestore(payload);
    });
  };
  window.addEventListener('pageshow', handleReturn);
  const restoreWhenReady = () => {
    if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') handleReturn({ persisted: false });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreWhenReady, { once: true });
  else restoreWhenReady();

  window.BsrCollectionScrollRestore = { beginRestore, stopRestore };
})();
